import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTools } from '../components/ToolsContext';
import logo from '../image/office.png';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import './Home.css';

// Skeleton Card Component
const SkeletonCard = () => (
    <Col lg={4} md={6}>
        <Card className="h-100 border-0 rounded-4 shadow-sm skeleton-card overflow-hidden">
            <Card.Body className="p-4 d-flex flex-column h-100">
                <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="skeleton-logo"></div>
                    <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-title"></div>
                <div className="skeleton-description"></div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <div className="skeleton-tag"></div>
                    <div className="skeleton-tag"></div>
                    <div className="skeleton-tag"></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                    <div className="skeleton-country"></div>
                    <div className="d-flex align-items-center gap-2">
                        <div className="skeleton-status"></div>
                        <div className="skeleton-button"></div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
);

function Home() {
    const navigate = useNavigate();
    const { allTools, loading, error, loadProgress, isStreaming } = useTools();
    const [activeCategory, setActiveCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredTools, setFilteredTools] = useState([]);
    const DISPLAY_LIMIT = 20;

    // Generate dynamic meta description based on current state
    const getMetaDescription = () => {
        if (filteredTools.length > 0) {
            return `Discover ${filteredTools.length} AI tools including ${filteredTools.slice(0, 3).map(t => t.Name).join(', ')} and more. Find the best artificial intelligence solutions for your needs.`;
        }
        return "Discover 16,000+ AI tools across 13+ categories. Compare pricing, features, and find free alternatives. The ultimate AI tools directory for creators, developers, and businesses.";
    };

    // JSON-LD structured data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Koru AI",
        "url": "https://koru-ai.onrender.com",
        "description": "Discover and compare 16,000+ AI tools. Find the best artificial intelligence solutions across 13+ categories.",
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://koru-ai.onrender.com/?search={search_term_string}"
            },
            "query-input": "required name=search_term_string"
        }
    };

    const softwareSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Koru AI Tools Directory",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "Comprehensive directory of AI tools with 16,000+ listings, 13+ categories, and advanced filtering.",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "1250"
        }
    };

    // Apply filters
    useEffect(() => {
        if (!allTools.length) return;

        let results = [...allTools];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            results = results.filter(tool =>
                (tool.Name && tool.Name.toLowerCase().includes(query)) ||
                (tool['Short Description'] && tool['Short Description'].toLowerCase().includes(query)) ||
                (tool.Category && tool.Category.toLowerCase().includes(query)) ||
                (tool['Primary Task'] && tool['Primary Task'].toLowerCase().includes(query))
            );
        }

        setFilteredTools(results);
    }, [searchQuery, activeCategory, allTools]);

    const hasMore = filteredTools.length > DISPLAY_LIMIT;
    const displayedTools = filteredTools.slice(0, DISPLAY_LIMIT);

    const uniqueCategoriesCount = useMemo(() => {
        if (!allTools.length) return 0;
        const cats = new Set();
        allTools.forEach(tool => {
            const cat = tool.Category || tool.category || 'Uncategorized';
            cats.add(cat);
        });
        return cats.size;
    }, [allTools]);

    const isValidValue = (value) => {
        return value && value !== '' && value !== 0 && value !== '0' && value !== null && value !== undefined;
    };

    // Show skeleton cards when loading (no tools yet) or initial loading
    const showSkeleton = loading && allTools.length === 0;

    return (
        <>
            {/* Streaming indicator */}
            {isStreaming && allTools.length > 0 && (
                <div className="streaming-indicator" aria-live="polite">
                    <small className="text-muted">Loading more tools... ({allTools.length.toLocaleString()} loaded so far)</small>
                </div>
            )}

            <main className="container py-1">
                {/* Hero Section - Improved semantics */}
                <section className="container mt-5" aria-label="Hero section">
                    <div className="text-center mb-5 px-3">
                        <h1 className='display-5 fw-bold mb-3'>Discover Your Ideal <span className="text-primary">AI Solution</span></h1>
                        <p className="lead text-muted mb-4">
                            Find, compare, and select the perfect AI tools tailored to your requirements
                            from our curated database of{' '}
                            <strong>16k+ AI tools </strong>
                            across <strong>13+ categories</strong>.
                        </p>
                    </div>
                </section>

                {/* Tools Grid Section */}
                <section className="container mt-5" aria-label="AI tools directory">
                    {error ? (
                        <div className="alert alert-danger text-center" role="alert">
                            {error}
                            <button
                                className="btn btn-outline-danger ms-3"
                                onClick={() => window.location.reload()}
                                aria-label="Retry loading tools"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h2 className="h5 mb-0">
                                    {searchQuery && <span className="badge bg-secondary ms-2">Search: "{searchQuery}"</span>}
                                    {activeCategory && <span className="badge bg-info ms-2">Category: {activeCategory}</span>}
                                </h2>
                            </div>

                            <Row className="g-4">
                                {showSkeleton ? (
                                    // Show 6 skeleton cards while loading (2 rows of 3 on lg screens)
                                    Array.from({ length: 6 }).map((_, idx) => <SkeletonCard key={idx} />)
                                ) : displayedTools.length > 0 ? (
                                    displayedTools.map((tool, index) => (
                                        <Col key={index} lg={4} md={6}>
                                            <Card className="h-100 border-0 rounded-4 shadow-sm hover-card overflow-hidden">
                                                <Card.Body className="p-4 d-flex flex-column h-100">
                                                    <div className="d-flex align-items-start justify-content-between mb-3">
                                                        {tool.Logo && isValidValue(tool.Logo) && (
                                                            <img src={tool.Logo}
                                                                alt={`${tool.Name} logo`}
                                                                className="rounded-3 me-3"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = 'https://placehold.co/48x48?text=AI';
                                                                }}
                                                                loading="lazy"
                                                            />
                                                        )}
                                                        {typeof tool['Year Founded'] === 'number' && tool['Year Founded'] > 0 && (
                                                            <span className="badge bg-light text-dark border rounded-pill px-3 py-2 fs-7">
                                                                Founded {tool['Year Founded']}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h3 className="card-title h5 fw-bold mb-2">{tool.Name}</h3>

                                                    <p className="text-secondary small mb-3 description-clamp">
                                                        {tool['Short Description'] || 'No description available.'}
                                                    </p>

                                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                                        {tool.Category && isValidValue(tool.Category) && (
                                                            <span className="badge rounded-pill px-3 py-2 tag-badge" aria-label={`Category: ${tool.Category}`}>
                                                                #{tool.Category.toUpperCase()}
                                                            </span>
                                                        )}
                                                        {tool['Primary Task'] && isValidValue(tool['Primary Task']) && (
                                                            <span className="badge rounded-pill px-3 py-2 tag-badge" aria-label={`Primary task: ${tool['Primary Task']}`}>
                                                                #{tool['Primary Task'].toUpperCase()}
                                                            </span>
                                                        )}
                                                        {tool.industry && isValidValue(tool.industry) && (
                                                            <span className="badge rounded-pill px-3 py-2 tag-badge" aria-label={`Industry: ${tool.industry}`}>
                                                                #{tool.industry.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                                                        {tool.Country && isValidValue(tool.Country) && (
                                                            <span className="text-muted small" aria-label={`Country: ${tool.Country}`}>
                                                                <i className="bi bi-geo-alt me-1"></i> {tool.Country}
                                                            </span>
                                                        )}
                                                        <div className="d-flex align-items-center gap-2">
                                                            {tool['Website Status'] && tool['Website Status'] !== 'Error 403' && tool['Website Status'] !== '' && (
                                                                <span className={`badge rounded-pill px-3 py-2 ${tool['Website Status'].toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`}>
                                                                    {tool['Website Status']}
                                                                </span>
                                                            )}
                                                            <a
                                                                href={tool.Website || tool.Link || '#'}
                                                                target="_blank"
                                                                rel="noopener noreferrer nofollow"
                                                                className="btn btn-sm btn-outline-danger rounded-pill px-4 fw-semibold"
                                                                aria-label={`Visit ${tool.Name} website`}
                                                            >
                                                                Visit
                                                            </a>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col className="text-center py-5">
                                        <i className="bi bi-search display-4 text-muted mb-3"></i>
                                        <h2 className="h4">No tools found matching your criteria</h2>
                                        <p className="text-muted">Try adjusting your search or filters</p>
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setSearchQuery('');
                                                setActiveCategory(null);
                                            }}
                                            aria-label="Clear all filters"
                                        >
                                            Clear Filters
                                        </button>
                                    </Col>
                                )}
                            </Row>

                            {hasMore && !isStreaming && !showSkeleton && (
                                <div className="text-center mt-5">
                                    <button
                                        className="btn btn-primary btn-sm px-5 py-3 rounded-pill shadow-sm"
                                        onClick={() => navigate('/category')}
                                        aria-label="View all AI tools"
                                    >
                                        See more tools →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* About Section - SEO friendly */}
                <section className="row align-items-center mt-5 text-center text-lg-start" aria-label="About Koru AI">
                    <div className="col-lg-6 order-1 order-lg-1">
                        <h2 className='font1'>
                            The Fastest <span className="text-success">Way</span> To Find The Perfect AI Tool.
                        </h2>
                        <p>Explore <strong>16,000+ AI tools</strong> across <strong>13+ categories</strong> including chatbots, image generation, code assistants, video editing, audio tools, and productivity software.</p>
                        <p>Find free alternatives to paid tools, compare features, and read verified reviews.</p>
                    </div>
                    <div className="col-lg-6 order-2 order-lg-2 mt-4 mt-lg-0">
                        <img
                            src={logo}
                            alt="Koru AI - AI tools directory illustration"
                            className="img-fluid w-100"
                            style={{ maxWidth: '500px', width: '100%', height: 'auto', margin: '0 auto', display: 'block', borderRadius: '15px' }}
                            loading="lazy"
                        />
                    </div>
                </section>

                <hr className="my-4 w-50 justify-content-center mx-auto" />

                {/* Why Choose Us Section */}
                <section className='text-center mt-5 mb-5' aria-label="Why choose Koru AI">
                    <h2 className='fw-bold display-6 mb-4'>Why Choose Us?</h2>
                    <Row className="g-4 my-4 justify-content-center">
                        <Col lg={4} md={6}>
                            <Card className="h-100 shadow-lg border-0 rounded-4 overflow-hidden hover-effect">
                                <Card.Header className="bg-white fw-semibold fs-5 border-0 py-3 px-4 bg-gradient-light">
                                    <div className="d-flex align-items-center">
                                        <div className="icon-circle bg-primary-light me-3">
                                            <i className="bi bi-patch-check-fill text-primary"></i>
                                        </div>
                                        Verified & Updated Tools
                                    </div>
                                </Card.Header>
                                <Card.Body className="px-4 pb-4 pt-3">
                                    <p className="text-muted mb-0 fs-6">
                                        We rigorously test and maintain our database to list only real, working AI tools—including free, freemium, and discounted options—with up-to-date features, accurate pricing, and practical use-cases. No fluff or outdated information.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={4} md={6}>
                            <Card className="h-100 shadow-lg border-0 rounded-4 overflow-hidden hover-effect">
                                <Card.Header className="bg-white fw-semibold fs-5 border-0 py-3 px-4 bg-gradient-light">
                                    <div className="d-flex align-items-center">
                                        <div className="icon-circle bg-info-light me-3">
                                            <i className="bi bi-funnel-fill text-info"></i>
                                        </div>
                                        Smart Filters & Use-Cases
                                    </div>
                                </Card.Header>
                                <Card.Body className="px-4 pb-4 pt-3">
                                    <p className="text-muted mb-0 fs-6">
                                        Our intelligent filtering system helps you find tools by specific tasks—writing, coding, design, or productivity. Each listing includes real-world use cases, free plan limitations, and helpful prompt suggestions when relevant.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={4} md={6}>
                            <Card className="h-100 shadow-lg border-0 rounded-4 overflow-hidden hover-effect">
                                <Card.Header className="bg-white fw-semibold fs-5 border-0 py-3 px-4 bg-gradient-light">
                                    <div className="d-flex align-items-center">
                                        <div className="icon-circle bg-warning-light me-3">
                                            <i className="bi bi-gem text-warning"></i>
                                        </div>
                                        Exclusive Deals & Freebies
                                    </div>
                                </Card.Header>
                                <Card.Body className="px-4 pb-4 pt-3">
                                    <p className="text-muted mb-0 fs-6">
                                        We curate the best AI deals, highlight underrated gems, and uncover free alternatives to paid tools. Our team updates these daily so you'll never miss valuable opportunities to save money while getting premium tools.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </section>
            </main>
        </>
    );
}

export default Home;